import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cihazIDService, kullaniciService } from '../api/dbService';
import PageLayout from '../components/PageLayout';
import LoadingSpinner from '../components/LoadingSpinner';

const EMPTY_CIHAZ = { device_id: '', cihaz_adi: '', cihaz_modeli: '', durum: 'aktif', kontor_bitis_tarihi: '' };

export default function Devices() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('current_user') || '{}');

  const [cihazlar, setCihazlar] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [ekleModal, setEkleModal] = useState(false);
  const [ekleForm, setEkleForm] = useState(EMPTY_CIHAZ);
  const [ekleLoading, setEkleLoading] = useState(false);

  const [konturModal, setKonturModal] = useState(null);
  const [gun, setGun] = useState(30);

  const [ataModal, setAtaModal] = useState(null); // cihaz id
  const [ataKullaniciId, setAtaKullaniciId] = useState('');
  const [atanenler, setAtananlar] = useState([]);
  const [ataLoading, setAtaLoading] = useState(false);

  const load = useCallback(async () => {
    const [cRes, kRes] = await Promise.all([
      cihazIDService.getCihazlar(),
      kullaniciService.getKullanicilar()
    ]);
    setCihazlar(cRes.data || []);
    setKullanicilar(kRes.data || []);
  }, []);

  useEffect(() => {
    if (user.role !== 'admin') { navigate('/'); return; }
    load().finally(() => setLoading(false));
  }, []);

  async function handleEkle(e) {
    e.preventDefault();
    if (!ekleForm.device_id.trim()) return toast.error('Device ID zorunlu');
    setEkleLoading(true);
    try {
      await cihazIDService.addCihaz({
        device_id: ekleForm.device_id.trim(),
        cihaz_adi: ekleForm.cihaz_adi,
        cihaz_modeli: ekleForm.cihaz_modeli,
        durum: ekleForm.durum,
        kontor_bitis_tarihi: ekleForm.kontor_bitis_tarihi || null
      });
      toast.success('Cihaz eklendi');
      setEkleModal(false);
      setEkleForm(EMPTY_CIHAZ);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ekleme başarısız');
    } finally {
      setEkleLoading(false);
    }
  }

  async function handleKontur() {
    try {
      await cihazIDService.konturUzat(konturModal, gun);
      toast.success(`${gun} gün eklendi`);
      setKonturModal(null);
      await load();
    } catch { toast.error('İşlem başarısız'); }
  }

  async function openAtaModal(cihazId) {
    setAtaModal(cihazId);
    setAtaKullaniciId('');
    setAtaLoading(true);
    try {
      const res = await cihazIDService.getCihazKullanicilar(cihazId);
      setAtananlar(res.data || []);
    } catch { setAtananlar([]); }
    finally { setAtaLoading(false); }
  }

  async function handleAta() {
    if (!ataKullaniciId) return toast.error('Kullanıcı seçin');
    try {
      await cihazIDService.ataCihazKullanici(ataModal, ataKullaniciId);
      toast.success('Kullanıcı atandı');
      const res = await cihazIDService.getCihazKullanicilar(ataModal);
      setAtananlar(res.data || []);
      setAtaKullaniciId('');
      await load();
    } catch (err) { toast.error(err.response?.data?.message || 'Atama başarısız'); }
  }

  async function handleAtaKaldir(uid) {
    try {
      await cihazIDService.cihazKullaniciSil(ataModal, uid);
      toast.success('Atama kaldırıldı');
      const res = await cihazIDService.getCihazKullanicilar(ataModal);
      setAtananlar(res.data || []);
      await load();
    } catch { toast.error('İşlem başarısız'); }
  }

  const atamaYapilmamislar = kullanicilar.filter(k => !atanenler.find(a => a.id === k.id));

  if (loading) return <PageLayout><LoadingSpinner /></PageLayout>;

  return (
    <PageLayout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-semibold mb-0">Cihaz Yönetimi</h5>
        <button className="btn btn-brand btn-sm" onClick={() => { setEkleForm(EMPTY_CIHAZ); setEkleModal(true); }}>
          + Cihaz Ekle
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>Device ID</th>
              <th>Adı</th>
              <th>Durum</th>
              <th>Kontör Bitiş</th>
              <th>Atanan Kullanıcılar</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cihazlar.map(c => (
              <tr key={c.id}>
                <td><code className="small">{c.device_id}</code></td>
                <td>{c.cihaz_adi || '—'}</td>
                <td>
                  <span className={`badge bg-${c.durum === 'aktif' ? 'success' : c.durum === 'bakimda' ? 'warning' : 'secondary'}`}>
                    {c.durum}
                  </span>
                </td>
                <td className="small">{c.kontor_bitis_tarihi ? new Date(c.kontor_bitis_tarihi).toLocaleDateString('tr-TR') : '—'}</td>
                <td className="small text-muted">{c.atanan_kullanicilar || '—'}</td>
                <td>
                  <div className="d-flex gap-1">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setKonturModal(c.id)}>
                      Kontör
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => openAtaModal(c.id)}>
                      Kullanıcı
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {cihazlar.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">
                Henüz cihaz yok. "Cihaz Ekle" ile başlayın.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cihaz Ekle Modal */}
      {ekleModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Yeni Cihaz Ekle</h6>
                <button className="btn-close" onClick={() => setEkleModal(false)} />
              </div>
              <form onSubmit={handleEkle}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Device ID <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" placeholder="Elitech cihaz ID'si"
                      value={ekleForm.device_id} onChange={e => setEkleForm(f => ({ ...f, device_id: e.target.value }))} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Cihaz Adı</label>
                    <input className="form-control form-control-sm" placeholder="İsteğe bağlı"
                      value={ekleForm.cihaz_adi} onChange={e => setEkleForm(f => ({ ...f, cihaz_adi: e.target.value }))} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Model</label>
                    <input className="form-control form-control-sm" placeholder="Örn: RC-4"
                      value={ekleForm.cihaz_modeli} onChange={e => setEkleForm(f => ({ ...f, cihaz_modeli: e.target.value }))} />
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Durum</label>
                      <select className="form-select form-select-sm"
                        value={ekleForm.durum} onChange={e => setEkleForm(f => ({ ...f, durum: e.target.value }))}>
                        <option value="aktif">Aktif</option>
                        <option value="pasif">Pasif</option>
                        <option value="bakimda">Bakımda</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-semibold">Kontör Bitiş</label>
                      <input type="date" className="form-control form-control-sm"
                        value={ekleForm.kontor_bitis_tarihi}
                        onChange={e => setEkleForm(f => ({ ...f, kontor_bitis_tarihi: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEkleModal(false)}>İptal</button>
                  <button type="submit" className="btn btn-brand btn-sm" disabled={ekleLoading}>
                    {ekleLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    Ekle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Kontör Modal */}
      {konturModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 340 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Kontör Uzat</h6>
                <button className="btn-close" onClick={() => setKonturModal(null)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">Gün Sayısı</label>
                <select className="form-select" value={gun} onChange={e => setGun(Number(e.target.value))}>
                  {[5, 10, 20, 30, 90, 180, 360].map(g => <option key={g} value={g}>{g} gün</option>)}
                </select>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setKonturModal(null)}>İptal</button>
                <button className="btn btn-brand btn-sm" onClick={handleKontur}>Ekle</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Atama Modal */}
      {ataModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Kullanıcı Ata</h6>
                <button className="btn-close" onClick={() => setAtaModal(null)} />
              </div>
              <div className="modal-body">
                {ataLoading ? <LoadingSpinner /> : <>
                  {atanenler.length > 0 && (
                    <div className="mb-3">
                      <small className="text-muted fw-semibold d-block mb-1">Atanmış Kullanıcılar</small>
                      <ul className="list-group list-group-flush">
                        {atanenler.map(k => (
                          <li key={k.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                            <span className="small">{k.username} <span className="text-muted">({k.email})</span></span>
                            <button className="btn btn-outline-danger btn-sm py-0 px-1"
                              onClick={() => handleAtaKaldir(k.id)}>✕</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm flex-grow-1"
                      value={ataKullaniciId} onChange={e => setAtaKullaniciId(e.target.value)}>
                      <option value="">— Kullanıcı seç —</option>
                      {atamaYapilmamislar.map(k => (
                        <option key={k.id} value={k.id}>{k.username} ({k.email})</option>
                      ))}
                    </select>
                    <button className="btn btn-brand btn-sm" onClick={handleAta}>Ata</button>
                  </div>
                </>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setAtaModal(null)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
